import { invokeFunction, restList, rpc } from "@/lib/http";
import { supabase } from "@/lib/supabase";
import type { AppSettings, DashboardSummary, Profile, SigningKeyRow } from "@/types/domain";

// -----------------------------------------------------------------------------
//  Gösterge paneli
// -----------------------------------------------------------------------------

export async function getDashboardSummary(): Promise<DashboardSummary> {
  return rpc<DashboardSummary>("dashboard_summary");
}

// -----------------------------------------------------------------------------
//  Hesaplar
// -----------------------------------------------------------------------------

export async function listProfiles(): Promise<Profile[]> {
  return restList<Profile>("profiles", { select: "*", order: "full_name.asc" });
}

/**
 * Yeni hesap açar.
 *
 * `auth.users`a yazmak service_role gerektirdiği için Edge Function'da.
 * Profil satırını `on_auth_user_created` tetikleyicisi oluşturuyor.
 */
export async function createUser(input: {
  email: string;
  password: string;
  fullName: string;
  isAdmin: boolean;
}): Promise<{ message: string }> {
  return invokeFunction<{ message: string }>("admin-users", {
    action: "create",
    email: input.email,
    password: input.password,
    full_name: input.fullName,
    is_admin: input.isAdmin,
  });
}

export async function resetUserPassword(userId: string, password: string): Promise<{ message: string }> {
  return invokeFunction<{ message: string }>("admin-users", {
    action: "reset_password",
    user_id: userId,
    password,
  });
}

export async function setProfileAdmin(id: string, isAdmin: boolean): Promise<void> {
  await rpc<void>("set_profile_admin", { p_id: id, p_is_admin: isAdmin });
}

export async function setProfileActive(id: string, isActive: boolean): Promise<void> {
  await rpc<void>("set_profile_active", { p_id: id, p_is_active: isActive });
}

export async function updateMyProfile(fullName: string): Promise<Profile> {
  return rpc<Profile>("update_my_profile", { p_full_name: fullName });
}

/**
 * Kendi parolasını değiştirir.
 *
 * Bu tek işlem axios yerine supabase-js üzerinden gidiyor: parola değişimi
 * oturumu tazeliyor ve yeni jetonun istemcinin depolamasına yazılması gerekiyor,
 * bunu doğrudan HTTP çağrısıyla yapmak oturum yönetimini ikiye bölerdi.
 *
 * Mevcut parola, aynı e-postayla yeniden giriş yapılarak doğrulanıyor —
 * Supabase Auth'ta "eski parolayı da gönder" diye bir uç nokta yok ve
 * doğrulamadan değiştirmek açık bir sekmeyi ele geçiren için hediye olurdu.
 */
export async function changeMyPassword(input: {
  email: string;
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  const { error: signInError } = await supabase.auth.signInWithPassword({
    email: input.email,
    password: input.currentPassword,
  });

  if (signInError) throw new Error("Mevcut parola hatalı.");

  const { error } = await supabase.auth.updateUser({ password: input.newPassword });

  if (error) {
    throw new Error(
      /different from the old/i.test(error.message)
        ? "Yeni parola mevcut parolayla aynı olamaz."
        : `Parola güncellenemedi: ${error.message}`,
    );
  }
}

// -----------------------------------------------------------------------------
//  İmza anahtarı
// -----------------------------------------------------------------------------

/**
 * Anahtarın AÇIK yarısı. Özel anahtar veritabanında yok — Edge Function gizli
 * değeri olarak duruyor ve kurulumda bir kez tanımlanıyor.
 */
export async function listSigningKeys(): Promise<SigningKeyRow[]> {
  return restList<SigningKeyRow>("signing_keys", {
    select: "*",
    order: "is_active.desc,created_at.desc",
  });
}

// -----------------------------------------------------------------------------
//  Ayarlar
// -----------------------------------------------------------------------------

export async function getAppSettings(): Promise<AppSettings> {
  const rows = await restList<AppSettings>("app_settings", { select: "*", limit: 1 });

  if (rows.length === 0) {
    throw new Error("Ayar kaydı bulunamadı. Şema göçü eksik olabilir.");
  }

  return rows[0];
}

export async function updateAppSettings(input: {
  expiryWarningDays: number;
  defaultValidityDays: number;
}): Promise<AppSettings> {
  return rpc<AppSettings>("update_app_settings", {
    p_expiry_warning_days: input.expiryWarningDays,
    p_default_validity_days: input.defaultValidityDays,
  });
}

/** Başarılı girişten sonra çağrılır: son giriş zamanı + denetim izi. */
export async function recordLogin(): Promise<void> {
  await rpc<void>("record_login");
}
